/*
 * <cart-free-gift> — keeps a free-gift line in sync with the cart.
 *
 * Approach: the gift is your REAL product, made free by a native automatic
 * "Buy X Get Y" discount (spend >= threshold -> 1 of [product] free), set up in
 * admin. This element only ensures one unit is in the cart (tagged with the _gift
 * line property) while the cart qualifies, and pulls it out when it doesn't.
 * Shopify's discount decides the price, so free-ness/eligibility is enforced
 * server-side: a forced add below threshold is simply charged full price.
 *
 * Not-misleading guarantee: after adding the gift we re-read the cart total; if it
 * went UP, the discount didn't make it free, so we remove the line immediately. A
 * gift line therefore only ever persists when it is genuinely free.
 *
 * The gift is added with properties._gift so it (a) becomes a SEPARATE line from
 * any paid line of the same product and (b) is identifiable for display/removal.
 * We detect the gift by that property, never by variant id (the same variant can
 * also be a paid line).
 *
 * Mounted once outside #CartDrawer so it survives cart re-renders. Loop-safe:
 * idempotent (acts only on a mismatch), a re-entrancy lock while a request is in
 * flight, and source-tagged cart-update events it ignores from itself.
 *
 * Uses the same routes + fetchConfig + pub/sub as the rest of Dawn (global.js,
 * constants.js, pubsub.js, cart.js all load first).
 */
class CartFreeGift extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold, 10);
    // data-threshold is shop-currency cents, but the cart API returns presentment
    // cents. Convert with the same live rate Shopify uses for the Buy-X-Get-Y
    // discount's own minimum at checkout (window.Shopify.currency is undocumented
    // but long-stable; without it the misleading-add guard below still protects).
    const fx = window.Shopify && window.Shopify.currency;
    if (fx && fx.rate && fx.active && fx.active !== this.dataset.shopCurrency) {
      this.threshold = Math.round(this.threshold * parseFloat(fx.rate));
    }
    this.variantId = parseInt(this.dataset.variantId, 10);
    this.busy = false;
    this.onCartUpdate = (event) => {
      if (event && event.source === 'cart-free-gift') return;
      this.reconcile();
    };
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
    this.reconcile();
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  // Pure steady-state decision. Side-effect-free so the threshold logic is testable.
  static decide(subtotal, threshold, giftLine) {
    const qualifies = subtotal >= threshold;
    if (qualifies && !giftLine) return 'add';
    if (!qualifies && giftLine) return 'remove';
    if (giftLine && giftLine.quantity !== 1) return 'fix-qty';
    return 'none';
  }

  static findGift(cart) {
    return cart.items.find((item) => item.properties && item.properties._gift);
  }

  async reconcile() {
    if (this.busy || !this.variantId || !this.threshold) return;
    this.busy = true;
    try {
      const cart = await this.getCart();
      const giftLine = CartFreeGift.findGift(cart);
      const action = CartFreeGift.decide(cart.items_subtotal_price, this.threshold, giftLine);

      if (action === 'add') {
        await this.addFreeGift(cart.total_price);
      } else if (action === 'remove') {
        await this.mutate(routes.cart_change_url, { id: giftLine.key, quantity: 0 });
      } else if (action === 'fix-qty') {
        await this.mutate(routes.cart_change_url, { id: giftLine.key, quantity: 1 });
      }
    } catch (e) {
      // gift is non-critical; swallow and let the next cart-update retry.
    } finally {
      this.busy = false;
    }
  }

  // Add the gift, then confirm the discount actually made it free.
  async addFreeGift(totalBefore) {
    await this.mutate(routes.cart_add_url, {
      id: this.variantId,
      quantity: 1,
      properties: { _gift: true },
    });
    const cart = await this.getCart();
    const giftLine = CartFreeGift.findGift(cart);
    if (giftLine && cart.total_price > totalBefore) {
      // The Buy-X-Get-Y discount didn't apply (cart ineligible, or the discount is
      // missing/disabled). Never charge for a "gift" — pull it straight back out.
      console.warn('[cart-free-gift] gift was not free (no matching automatic discount?); removed it.');
      await this.mutate(routes.cart_change_url, { id: giftLine.key, quantity: 0 });
    }
  }

  async getCart() {
    return (await fetch(`${routes.cart_url}.js`)).json();
  }

  async mutate(url, payload) {
    const sections = CartFreeGift.SECTIONS;
    const body = JSON.stringify({
      ...payload,
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const response = await fetch(url, { ...fetchConfig(), body });
    const parsed = await response.json();
    if (parsed.sections) this.renderSections(parsed.sections, sections);
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-free-gift', cartData: parsed });
  }

  renderSections(rendered, sections) {
    sections.forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return; // section not on this page
      const html = rendered[section.section];
      if (html == null) return; // bundled section render can be null on HTTP 200
      const target = container.querySelector(section.selector) || container;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
  }
}

// Every cart surface the gift can change: the drawer (always present), the header
// count bubble, and the standalone /cart page. Max 5 per the Section Rendering API.
CartFreeGift.SECTIONS = [
  { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
  { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
  { id: 'main-cart-items', section: 'main-cart-items', selector: '.js-contents' },
  { id: 'main-cart-footer', section: 'main-cart-footer', selector: '.js-contents' },
];

customElements.define('cart-free-gift', CartFreeGift);
